import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Send, Sparkles, Trash2, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  exploreGeoPrompt,
  fetchGeoExploreHistory,
  deleteGeoExplore,
} from "@/lib/api";
import PromptResponsesBlock from "./PromptResponsesBlock";

const ENGINE_LABELS = {
  chatgpt: "ChatGPT",
  claude: "Claude",
  gemini: "Gemini",
  perplexity: "Perplexity",
};
const ALL_ENGINES = Object.keys(ENGINE_LABELS);

export default function ExplorePanel({ clientId, clientName }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [promptType, setPromptType] = useState("category");
  const [selectedEngines, setSelectedEngines] = useState(ALL_ENGINES);
  const [latestResult, setLatestResult] = useState(null);

  const historyQ = useQuery({
    queryKey: ["geo-explore-history", clientId],
    queryFn: () => fetchGeoExploreHistory(clientId, 20),
  });

  const mut = useMutation({
    mutationFn: () =>
      exploreGeoPrompt(clientId, {
        text: text.trim(),
        prompt_type: promptType,
        engines: selectedEngines,
      }),
    onSuccess: (res) => {
      setLatestResult(res);
      qc.invalidateQueries({ queryKey: ["geo-explore-history", clientId] });
      toast.success("Respuestas recibidas");
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.detail || err?.message || "Error al ejecutar"
      );
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id) => deleteGeoExplore(clientId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["geo-explore-history", clientId] });
      toast.success("Eliminado del historial");
    },
  });

  const toggleEngine = (e) => {
    setSelectedEngines((prev) =>
      prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]
    );
  };

  return (
    <div className="space-y-6" data-testid="geo-explore-panel">
      {/* HEADER */}
      <div className="rounded-md border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50/40 p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="h-9 w-9 rounded-md bg-violet-600 text-white flex items-center justify-center shrink-0">
            <Sparkles className="h-4.5 w-4.5" />
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold text-slate-900">
              Explore — laboratorio de prompts
            </h2>
            <p className="text-sm text-slate-600 mt-0.5">
              Probá cualquier pregunta contra ChatGPT, Claude, Gemini y Perplexity con búsqueda web activa. Las respuestas se guardan en historial para que puedas comparar después.
            </p>
          </div>
        </div>

        {/* INPUT */}
        <div className="space-y-3">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder={`Ej: ¿Cuáles son los mejores servicios de catering corporativo en CABA? · ¿Qué dicen de ${clientName} sus clientes? · ¿Cuáles son las tendencias 2026 en alimentación corporativa?`}
            className="bg-white"
            data-testid="geo-explore-input"
          />

          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              {/* prompt type */}
              <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md p-0.5">
                {[
                  { v: "category", l: "Categoría" },
                  { v: "brand", l: "Marca" },
                ].map((t) => (
                  <button
                    key={t.v}
                    onClick={() => setPromptType(t.v)}
                    className={`text-xs px-2.5 py-1 rounded-sm font-medium transition ${
                      promptType === t.v
                        ? "bg-violet-600 text-white"
                        : "text-slate-600 hover:bg-slate-100"
                    }`}
                    data-testid={`geo-explore-type-${t.v}`}
                  >
                    {t.l}
                  </button>
                ))}
              </div>

              {/* engine selector */}
              <div className="flex items-center gap-1">
                {ALL_ENGINES.map((e) => (
                  <button
                    key={e}
                    onClick={() => toggleEngine(e)}
                    className={`text-xs px-2 py-1 rounded-sm border font-medium transition ${
                      selectedEngines.includes(e)
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                    }`}
                    data-testid={`geo-explore-engine-${e}`}
                  >
                    {ENGINE_LABELS[e]}
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={() => mut.mutate()}
              disabled={!text.trim() || !selectedEngines.length || mut.isPending}
              className="bg-violet-600 hover:bg-violet-700 text-white gap-2"
              data-testid="geo-explore-fire"
            >
              {mut.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Consultando {selectedEngines.length} motores…
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Disparar a {selectedEngines.length} motores
                </>
              )}
            </Button>
          </div>

          {mut.isPending && (
            <div className="text-xs text-slate-500 italic">
              Las respuestas con web search tardan entre 30-90 segundos por motor. Paciencia.
            </div>
          )}
        </div>
      </div>

      {/* LATEST RESULT */}
      {latestResult && (
        <section data-testid="geo-explore-latest">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-2">
            Última consulta
          </h3>
          <PromptResponsesBlock
            prompt={{
              text: latestResult.text,
              prompt_type: latestResult.prompt_type,
            }}
            rows={latestResult.results || []}
            defaultOpen
          />
        </section>
      )}

      {/* HISTORY */}
      <section>
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-2">
          Historial de exploración
        </h3>
        {historyQ.isLoading ? (
          <div className="text-sm text-slate-500">Cargando…</div>
        ) : !(historyQ.data || []).length ? (
          <div className="rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
            Todavía no hiciste ninguna consulta. Probá una arriba.
          </div>
        ) : (
          <div className="space-y-2">
            {(historyQ.data || [])
              .filter((h) => !latestResult || h.id !== latestResult.id)
              .map((h) => (
                <HistoryItem
                  key={h.id}
                  item={h}
                  onDelete={() => deleteMut.mutate(h.id)}
                />
              ))}
          </div>
        )}
      </section>
    </div>
  );
}

function HistoryItem({ item, onDelete }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-md border border-slate-200 bg-white overflow-hidden"
      data-testid="geo-explore-history-item"
    >
      <div className="px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex-1 flex items-center gap-3 min-w-0 text-left"
        >
          {open ? (
            <ChevronDown className="h-4 w-4 text-slate-500 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-800 truncate">
              {item.text}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {new Date(item.created_at).toLocaleString("es-AR", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}{" "}
              · {(item.results || []).length} respuestas · tipo:{" "}
              <span className="font-medium">{item.prompt_type}</span>
            </div>
          </div>
        </button>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-rose-500 hover:text-rose-700 shrink-0"
          onClick={() => {
            if (window.confirm("¿Eliminar esta consulta del historial?")) onDelete();
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      {open && (
        <div className="border-t border-slate-100 p-3 bg-slate-50/40">
          <PromptResponsesBlock
            prompt={{ text: item.text, prompt_type: item.prompt_type }}
            rows={item.results || []}
            defaultOpen
          />
        </div>
      )}
    </div>
  );
}
